import { FC, ReactNode, useRef, useState } from 'react';
import {
	Accordion,
	accordionClasses,
	AccordionDetails,
	AccordionProps,
	AccordionSummary,
	Box,
	IconButton,
	List,
	ListItem,
	ListItemButton,
	ListItemProps,
	ListItemText,
	Menu,
	Stack,
	StackProps,
	SxProps
} from '@mui/material';
import { VoltaageCheckbox } from '@components/common';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';

const ACCORDION_PADDING_Y = 24;
const ACCORDION_PADDING_LEFT = 48;

interface IActionDefinition {
	hidden?: boolean;
	disabled?: boolean;
	text?: ReactNode;
	icon?: ReactNode;
	onClick?: () => void;
}

export interface IEntityListItemProps extends ListItemProps {
	actionsType?: 'icon' | 'menu';
	actions?: IActionDefinition[];
	notSelectable?: boolean;
	selected?: boolean;
	badge?: ReactNode;
	badgeSx?: SxProps;
	cardProps?: Omit<AccordionProps, 'children'>;
	actionIconsContainerProps?: StackProps;
	disableExpandToggle?: boolean;
	expandedInitially?: boolean;
	expandedContent?: ReactNode;
	accordionTrigger?: 'content' | 'icon-button';
	onSelectChange?: (selected: boolean) => void;
}

export const EntityListItem: FC<IEntityListItemProps> = ({
	actionsType = 'menu',
	actions = [],
	notSelectable,
	selected,
	badge,
	badgeSx,
	cardProps: { sx: cardSx, ...accordionProps } = {},
	actionIconsContainerProps,
	disableExpandToggle,
	expandedInitially,
	expandedContent,
	accordionTrigger = 'content',
	onSelectChange,
	children,
	sx,
	...props
}) => {
	const menuRef = useRef<HTMLButtonElement>(null);
	const [menuVisibility, setMenuVisibility] = useState(false);
	const [accordionExpanded, setAccordionExpanded] = useState(expandedInitially);
	const hasActions = !!actions?.filter((act) => !act.hidden).length;
	const isActionsDisplayedAs = (type: IEntityListItemProps['actionsType']) => hasActions && actionsType === type;
	const hideMenu = () => setMenuVisibility(false);
	const setContentVisible = (visible: boolean) => expandedContent && !disableExpandToggle && setAccordionExpanded(visible);

	return (
		<ListItem disableGutters sx={{ backgroundColor: 'transparent', paddingY: 0.5, ...sx }} {...props}>
			<Stack direction="row" alignItems="center" sx={{ flexGrow: 1 }} spacing={3}>
				{!notSelectable ? (
					<VoltaageCheckbox
						name="entity-list-select-checkbox"
						data-testid="entity-list-select-checkbox"
						onChange={() => onSelectChange?.(!selected)}
						checked={selected}
					/>
				) : null}

				<Box
					sx={{ width: 1.0, cursor: expandedContent && accordionTrigger === 'content' ? 'pointer' : 'default' }}
					onClick={() => accordionTrigger === 'content' && setContentVisible(!accordionExpanded)}
				>
					<Accordion
						variant="outlined"
						disableGutters
						expanded={accordionExpanded}
						onChange={(_, expanded) => setContentVisible(expanded)}
						sx={{
							[`&.${accordionClasses.root}`]: {
								flexGrow: 1,
								paddingY: `${ACCORDION_PADDING_Y}px`,
								paddingLeft: `${ACCORDION_PADDING_LEFT}px`,
								paddingRight: hasActions ? 3 : 4,
								boxShadow: !notSelectable && selected ? '0 0 0 3px #cfd5ff, inset 0 0 0 2px #6176f4' : undefined,
								borderRadius: '20px !important'
							},
							...cardSx
						}}
						{...accordionProps}
					>
						<AccordionSummary
							sx={{
								position: 'relative',
								cursor:
									expandedContent && accordionTrigger === 'content' && !disableExpandToggle
										? undefined
										: 'default !important',
								padding: 0
							}}
						>
							{badge ? (
								<Box
									sx={{
										position: 'absolute',
										top: -1 * ACCORDION_PADDING_Y,
										left: -1 * ACCORDION_PADDING_LEFT,
										backgroundColor: 'primary.main',
										color: 'common.white',
										paddingX: 3,
										paddingY: 0.5,
										borderBottomRightRadius: 20,
										borderTopLeftRadius: 20,
										...badgeSx
									}}
								>
									{badge}
								</Box>
							) : null}
							<Stack direction="row" alignItems="center" sx={{ width: 1.0 }} spacing={2}>
								<Box display="flex" flexGrow={1}>
									{children}
								</Box>

								{expandedContent && accordionTrigger === 'icon-button' && (
									<IconButton onClick={() => setContentVisible(!accordionExpanded)}>
										{accordionExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
									</IconButton>
								)}
								<Box onClick={(e) => e.stopPropagation()}>
									{isActionsDisplayedAs('menu') && (
										<>
											<IconButton
												data-testid="entity-list-item-more-icon-button"
												ref={menuRef}
												onClick={() => setMenuVisibility(!menuVisibility)}
												sx={{ color: 'common.black' }}
											>
												<MoreVertIcon color="inherit" />
											</IconButton>
											<Menu
												anchorEl={menuRef.current}
												open={menuVisibility}
												onClose={hideMenu}
												transformOrigin={{ vertical: 4, horizontal: 170 }}
												PaperProps={{
													sx: { borderRadius: 1.5, backgroundColor: 'common.black', color: 'common.white' }
												}}
											>
												<List disablePadding sx={{ minWidth: 190 }}>
													{actions
														.filter((act) => !act.hidden)
														.map(({ text, icon, disabled, onClick }, i) => (
															<ListItemButton
																data-testid="entity-list-action-item-button"
																key={`action-${i}`}
																onClick={() => (hideMenu(), onClick?.())}
																disabled={disabled}
															>
																<ListItemText primary={text} />
																{icon}
															</ListItemButton>
														))}
												</List>
											</Menu>
										</>
									)}
									{isActionsDisplayedAs('icon') && (
										<Stack direction="row" spacing={2} {...actionIconsContainerProps}>
											{actions
												.filter((act) => !act.hidden && act.icon)
												.map(({ icon, onClick }, i) => (
													<Box key={`action-${i}`} onClick={onClick}>
														{icon}
													</Box>
												))}
										</Stack>
									)}
								</Box>
							</Stack>
						</AccordionSummary>
						{expandedContent && (
							<AccordionDetails sx={{ padding: 0, cursor: 'default' }} onClick={(e) => e.stopPropagation()}>
								{expandedContent}
							</AccordionDetails>
						)}
					</Accordion>
				</Box>
			</Stack>
		</ListItem>
	);
};
